import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Send,
  Mail,
  Image,
  Eye,
  Plus,
  Users,
  Calendar,
  Upload,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Newsletter {
  id: string;
  subject: string;
  content: string;
  image_url: string | null;
  created_at: string;
  sent_at: string | null;
  recipient_count: number;
}

export const NewsletterTab = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [subscribedCount, setSubscribedCount] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [previewNewsletter, setPreviewNewsletter] = useState<Newsletter | null>(null);
  const [newsletterToDelete, setNewsletterToDelete] = useState<Newsletter | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);

  const { toast } = useToast();

  useEffect(() => {
    loadNewsletters();
    loadSubscribedCount();
  }, []);

  const loadNewsletters = async () => {
    try {
      const { data, error } = await supabase
        .from("newsletters")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setNewsletters(data || []);
    } catch (error) {
      console.error("Error loading newsletters:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubscribedCount = async () => {
    try {
      // Count profiles that are not unsubscribed
      const { data: emailPrefs } = await supabase
        .from("email_preferences")
        .select("user_id")
        .eq("unsubscribed", false);

      const { count: profilesCount } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("is_blocked", false);

      // The subscribed count is profiles without unsubscribe preference
      setSubscribedCount(profilesCount || 0);
    } catch (error) {
      console.error("Error loading subscribed count:", error);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const fileExt = file.name.split(".").pop();
      const fileName = `newsletter_${Date.now()}.${fileExt}`;
      const filePath = `newsletters/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("gira-images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("gira-images")
        .getPublicUrl(filePath);

      setImageUrl(publicUrl);
      setImageFile(file);

      toast({
        title: "Imagem carregada",
        description: "A imagem foi carregada com sucesso",
      });
    } catch (error: any) {
      toast({
        title: "Erro no upload",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateNewsletter = async () => {
    if (!subject.trim() || !content.trim()) {
      toast({
        title: "Erro",
        description: "Preencha o assunto e o conteúdo",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("newsletters")
        .insert({
          subject,
          content,
          image_url: imageUrl || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      setNewsletters([data, ...newsletters]);
      setShowCreateDialog(false);
      resetForm();

      toast({
        title: "Comunicado criado",
        description: "O comunicado foi criado e está pronto para envio",
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSendNewsletter = async (newsletter: Newsletter) => {
    setIsSending(true);

    try {
      const response = await supabase.functions.invoke("send-newsletter", {
        body: {
          newsletterId: newsletter.id,
          subject: newsletter.subject,
          content: newsletter.content,
          imageUrl: newsletter.image_url,
        },
      });

      if (response.error) throw response.error;

      // Update newsletter as sent
      await supabase
        .from("newsletters")
        .update({
          sent_at: new Date().toISOString(),
          recipient_count: response.data?.recipientCount || 0,
        })
        .eq("id", newsletter.id);

      setNewsletters(
        newsletters.map((n) =>
          n.id === newsletter.id
            ? { ...n, sent_at: new Date().toISOString(), recipient_count: response.data?.recipientCount || 0 }
            : n
        )
      );

      setShowConfirmDialog(false);

      toast({
        title: "Comunicado enviado",
        description: `O comunicado foi enviado para ${response.data?.recipientCount || 0} membros`,
      });
    } catch (error: any) {
      toast({
        title: "Erro no envio",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const resetForm = () => {
    setSubject("");
    setContent("");
    setImageUrl("");
    setImageFile(null);
  };

  const handlePreview = (newsletter: Newsletter) => {
    setPreviewNewsletter(newsletter);
    setShowPreviewDialog(true);
  };

  const handleDeleteNewsletter = async (newsletter: Newsletter) => {
    setIsDeleting(true);

    try {
      const { error } = await supabase
        .from("newsletters")
        .delete()
        .eq("id", newsletter.id);

      if (error) throw error;

      setNewsletters(newsletters.filter((n) => n.id !== newsletter.id));
      setShowDeleteDialog(false);
      setNewsletterToDelete(null);

      toast({
        title: "Comunicado excluído",
        description: "O comunicado foi excluído com sucesso",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao excluir",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{subscribedCount}</p>
                <p className="text-sm text-muted-foreground">Inscritos Ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/10 rounded-full">
                <Mail className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{newsletters.filter((n) => n.sent_at).length}</p>
                <p className="text-sm text-muted-foreground">Enviadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-500/10 rounded-full">
                <Calendar className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{newsletters.filter((n) => !n.sent_at).length}</p>
                <p className="text-sm text-muted-foreground">Rascunhos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Newsletter */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>Comunicados</CardTitle>
              <CardDescription>Crie e envie comunicados para os membros</CardDescription>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Comunicado
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Criar Comunicado</DialogTitle>
                  <DialogDescription>
                    Crie um novo comunicado para enviar aos membros
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject">Assunto</Label>
                    <Input
                      id="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Assunto do email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="content">Conteúdo</Label>
                    <Textarea
                      id="content"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Digite o conteúdo da newsletter..."
                      rows={10}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Imagem (opcional)</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={isUploading}
                      />
                      {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                    </div>
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt="Preview"
                        className="max-h-40 rounded-lg object-cover"
                      />
                    )}
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleCreateNewsletter}>
                      Criar Comunicado
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {newsletters.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum comunicado criado ainda
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Assunto</TableHead>
                    <TableHead className="hidden sm:table-cell">Criado em</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell">Dest.</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newsletters.map((newsletter) => (
                    <TableRow key={newsletter.id}>
                      <TableCell className="font-medium max-w-[120px] sm:max-w-none truncate">{newsletter.subject}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {format(new Date(newsletter.created_at), "dd/MM/yyyy", { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        {newsletter.sent_at ? (
                          <Badge variant="default">Enviado</Badge>
                        ) : (
                          <Badge variant="secondary">Rascunho</Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {newsletter.sent_at ? newsletter.recipient_count : "-"}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePreview(newsletter)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!newsletter.sent_at && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setPreviewNewsletter(newsletter);
                              setShowConfirmDialog(true);
                            }}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setNewsletterToDelete(newsletter);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview do Comunicado</DialogTitle>
          </DialogHeader>
          {previewNewsletter && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Assunto:</p>
                <p className="font-semibold">{previewNewsletter.subject}</p>
              </div>
              {previewNewsletter.image_url && (
                <img
                  src={previewNewsletter.image_url}
                  alt="Newsletter"
                  className="w-full rounded-lg"
                />
              )}
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap">{previewNewsletter.content}</div>
              </div>
              <div className="p-4 border-t text-sm text-muted-foreground text-center">
                <p>Terreiro de Umbanda Pai Joaquim de Aruanda</p>
                <p className="mt-2">
                  <a href="#" className="underline">Cancelar inscrição</a>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Send Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Envio</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja enviar o comunicado "{previewNewsletter?.subject}" para {subscribedCount} membros inscritos?
              <br /><br />
              <strong>Esta ação não pode ser desfeita.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => previewNewsletter && handleSendNewsletter(previewNewsletter)}
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Comunicado</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o comunicado "{newsletterToDelete?.subject}"?
              {newsletterToDelete?.sent_at && (
                <>
                  <br /><br />
                  <strong>Este comunicado já foi enviado para {newsletterToDelete.recipient_count} membros.</strong>
                </>
              )}
              <br /><br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => newsletterToDelete && handleDeleteNewsletter(newsletterToDelete)}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};